import Cookies from "js-cookie";
import {
  clearLocalStoragePreservingAppKeys,
  isRememberMeEnabled,
} from "./rememberMe";

const TOKEN_COOKIE = "token";
const PLAYER_TOKEN_COOKIE = "playerAccessToken";
const TOKEN_EXPIRES_COOKIE = "tokenExpires";
const REFRESH_TOKEN_COOKIE = "refreshToken";
const TEMP_TOKEN_COOKIE = "tempToken";
const PLAYER_STORAGE_KEY = "player";
/** Per-tab marker. sessionStorage dies with the tab, so a reopened tab has none. */
const TAB_SESSION_KEY = "kuberaTabSession";
const SESSION_MINUTES = 60;
const REMEMBER_ME_DAYS = 30;

const JWT_PATTERN = /^eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/;

function findJwtDeep(value, depth = 0) {
  if (!value || depth > 6) return null;

  if (typeof value === "string") {
    const trimmed = value.trim().replace(/^Bearer\s+/i, "");
    return JWT_PATTERN.test(trimmed) ? trimmed : null;
  }

  if (typeof value !== "object") return null;

  for (const nested of Object.values(value)) {
    const found = findJwtDeep(nested, depth + 1);
    if (found) return found;
  }

  return null;
}

/** Extract refresh token from CRM / Keycloak API responses. */
export function extractRefreshToken(response) {
  if (!response) return null;

  const data = response.data ?? response;
  const candidates = [
    data?.refresh_token,
    data?.refreshToken,
    data?.data?.refresh_token,
    data?.data?.refreshToken,
  ];

  for (const value of candidates) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return null;
}

/** Extract access token from CRM API responses. */
export function extractAuthToken(response) {
  if (!response) return null;

  const data = response.data ?? response;
  const candidates = [
    data?.access_token,
    data?.accessToken,
    data?.data?.accessToken,
    data?.data?.access_token,
    data?.Authorization,
    data?.data?.Authorization,
    data?.token,
    data?.auth_token,
    data?.session_token,
    data?.player_token,
    data?.Authorization,
    data?.authorization,
    data?.jwt,
    data?.auth?.access_token,
    data?.auth?.token,
    data?.session?.access_token,
    data?.session?.token,
    data?.tokens?.access_token,
    data?.tokens?.accessToken,
    response?.access_token,
    response?.token,
    response?.auth_token,
  ];

  for (const value of candidates) {
    if (typeof value === "string" && value.trim()) {
      return value.trim().replace(/^Bearer\s+/i, "");
    }
  }

  return findJwtDeep(response);
}

export function getStoredAuthToken() {
  const token = Cookies.get(PLAYER_TOKEN_COOKIE) || Cookies.get(TOKEN_COOKIE);
  if (!token) return "";
  return decodeURIComponent(token);
}

export function getStoredRefreshToken() {
  const token = Cookies.get(REFRESH_TOKEN_COOKIE);
  if (!token) return "";
  return decodeURIComponent(token);
}

/**
 * Writes an auth cookie with a lifetime that matches the remember-me choice.
 * Remember me on  -> persistent cookie, survives a browser restart.
 * Remember me off -> browser-session cookie, dropped when the browser closes.
 */
function setAuthCookie(name, value, expirationDate) {
  if (isRememberMeEnabled()) {
    Cookies.set(name, value, { expires: expirationDate });
    return;
  }
  Cookies.set(name, value);
}

function markTabSession() {
  try {
    sessionStorage.setItem(TAB_SESSION_KEY, "1");
  } catch {
    // sessionStorage may be unavailable
  }
}

function hasTabSession() {
  try {
    return sessionStorage.getItem(TAB_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

function writeAccessTokenCookies(token, expirationDate) {
  const raw = String(token).trim().replace(/^Bearer\s+/i, "");

  setAuthCookie(TOKEN_COOKIE, raw, expirationDate);
  setAuthCookie(PLAYER_TOKEN_COOKIE, raw, expirationDate);
  setAuthCookie(TOKEN_EXPIRES_COOKIE, expirationDate, expirationDate);
  markTabSession();
}

export function persistAuthToken(token) {
  if (!token) return false;

  const expirationDate = new Date();
  expirationDate.setTime(
    expirationDate.getTime() + SESSION_MINUTES * 60 * 1000
  );

  writeAccessTokenCookies(token, expirationDate);

  return true;
}

export function hasAuthToken() {
  return Boolean(getStoredAuthToken());
}

/**
 * True when a stored token may be reused on app start.
 * Without remember me the session is scoped to the tab that logged in, so
 * reopening a closed tab lands on the login page.
 */
export function hasRestorableSession() {
  if (!hasAuthToken()) return false;
  if (isRememberMeEnabled()) return true;
  return hasTabSession();
}

/**
 * Drops an auth session that must not carry into this tab.
 * Without remember me the session belongs to the tab that logged in, so a
 * reopened tab starts logged out. Run this before the app renders, otherwise
 * components mount while the stale token is still readable.
 * Returns true when a session was discarded.
 */
export function enforceSessionScope() {
  if (!hasAuthToken()) return false;
  if (hasRestorableSession()) return false;

  clearAuthSession();
  clearLocalStoragePreservingAppKeys();
  return true;
}

/** Stores a refresh token on its own, honouring the remember-me lifetime. */
export function persistRefreshToken(refreshToken, response) {
  if (!refreshToken) return false;
  setAuthCookie(
    REFRESH_TOKEN_COOKIE,
    refreshToken,
    getRefreshExpirationDate(response)
  );
  return true;
}

/** Removes every auth artifact. Use on logout / expired or abandoned session. */
export function clearAuthSession() {
  Cookies.remove(TOKEN_COOKIE);
  Cookies.remove(PLAYER_TOKEN_COOKIE);
  Cookies.remove(TOKEN_EXPIRES_COOKIE);
  Cookies.remove(REFRESH_TOKEN_COOKIE);
  Cookies.remove(TEMP_TOKEN_COOKIE);
  try {
    sessionStorage.removeItem(TAB_SESSION_KEY);
    localStorage.removeItem(PLAYER_STORAGE_KEY);
  } catch {
    // storage may be unavailable
  }
}

export function ensureAuthTokenFromResponses(...responses) {
  for (const response of responses) {
    const token = extractAuthToken(response);
    if (token && persistAuthToken(token)) return token;
  }
  return null;
}

export function getAuthorizationHeader() {
  const token = getStoredAuthToken();
  if (!token) return null;
  return `Bearer ${token}`;
}

function getSessionExpirationDate(response) {
  const payload = response?.data ?? response;
  const expiresInSeconds =
    payload?.data?.expiresIn ?? payload?.expiresIn ?? payload?.expires_in;

  const expirationDate = new Date();
  if (expiresInSeconds && Number(expiresInSeconds) > 0) {
    expirationDate.setTime(
      expirationDate.getTime() + Number(expiresInSeconds) * 1000
    );
  } else {
    expirationDate.setTime(
      expirationDate.getTime() + SESSION_MINUTES * 60 * 1000
    );
  }

  return expirationDate;
}

/**
 * Refresh token lifetime. It must outlive the access token, otherwise there is
 * nothing left to renew the session with. Falls back to 30 days when the API
 * does not report refresh_expires_in.
 */
function getRefreshExpirationDate(response) {
  const payload = response?.data ?? response;
  const expiresInSeconds =
    payload?.data?.refreshExpiresIn ??
    payload?.refreshExpiresIn ??
    payload?.data?.refresh_expires_in ??
    payload?.refresh_expires_in;

  const expirationDate = new Date();
  if (expiresInSeconds && Number(expiresInSeconds) > 0) {
    expirationDate.setTime(
      expirationDate.getTime() + Number(expiresInSeconds) * 1000
    );
  } else {
    expirationDate.setTime(
      expirationDate.getTime() + REMEMBER_ME_DAYS * 24 * 60 * 60 * 1000
    );
  }

  return expirationDate;
}

/** Persist player profile from player-login API response. */
export function persistPlayerData(response) {
  const player = response?.data?.player;
  if (!player || typeof player !== "object") return null;

  try {
    localStorage.setItem(PLAYER_STORAGE_KEY, JSON.stringify(player));
  } catch {
    // localStorage may be unavailable
  }

  return player;
}

export function getStoredPlayerData() {
  try {
    const raw = localStorage.getItem(PLAYER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Persist access + refresh tokens from player-login API response. */
export function persistAuthSession(response) {
  const accessToken = extractAuthToken(response);
  const refreshToken = extractRefreshToken(response);
  const expirationDate = getSessionExpirationDate(response);

  if (accessToken) {
    writeAccessTokenCookies(accessToken, expirationDate);
  }

  if (refreshToken) {
    setAuthCookie(
      REFRESH_TOKEN_COOKIE,
      refreshToken,
      getRefreshExpirationDate(response)
    );
  }

  persistPlayerData(response);

  return { accessToken, refreshToken };
}
